'use client'

import { Fragment, useState } from 'react'
import {
  formatNeedCell,
  groupByCategory,
  needCellClass,
  type PlanLineRow,
} from '@/features/kitchen/productionPlanLayout'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'

type PlanProductionTableProps = {
  planLines: PlanLineRow[]
}

const NUM_COL_CLASS = 'p-2 text-right tabular-nums whitespace-nowrap'

export function PlanProductionTable({ planLines }: PlanProductionTableProps) {
  const groups = groupByCategory(planLines)
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})

  function toggleCategory(categoryName: string) {
    setOpenCategories((prev) => ({ ...prev, [categoryName]: !prev[categoryName] }))
  }

  return (
    <div className="overflow-x-auto border border-outline-variant rounded-lg">
      <table className="w-full table-fixed text-body-sm">
        <colgroup>
          <col className="w-[38%]" />
          <col className="w-[15.5%]" />
          <col className="w-[15.5%]" />
          <col className="w-[15.5%]" />
          <col className="w-[15.5%]" />
        </colgroup>
        <thead className="bg-surface-container">
          <tr>
            <th className="text-left p-2 font-semibold">{kitchenTerms.production.columns.product}</th>
            <th className="text-right p-2 font-semibold">{kitchenTerms.production.columns.stock}</th>
            <th className="text-right p-2 font-semibold">{kitchenTerms.production.columns.plan}</th>
            <th className="text-right p-2 font-semibold">{kitchenTerms.production.columns.need}</th>
            <th className="text-right p-2 font-semibold">{kitchenTerms.production.columns.actual}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const open = openCategories[group.categoryName] ?? false
            return (
              <Fragment key={group.categoryName}>
                <tr className="bg-surface-container border-t border-outline-variant">
                  <td colSpan={5} className="p-0">
                    <button
                      type="button"
                      onClick={() => toggleCategory(group.categoryName)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left min-h-[44px]"
                      aria-expanded={open}
                    >
                      <span className="material-symbols-outlined text-lg text-on-surface-variant shrink-0">
                        {open ? 'expand_less' : 'expand_more'}
                      </span>
                      <span className="text-body-sm font-semibold text-on-surface truncate">
                        {group.categoryName}
                      </span>
                      <span className="text-label-md text-on-surface-variant shrink-0">
                        {group.items.length} produk
                      </span>
                    </button>
                  </td>
                </tr>
                {open &&
                  group.items.map((line) => (
                    <tr key={line.sku} className="border-t border-surface-variant">
                      <td className="p-2 truncate">{line.sku}</td>
                      <td className={NUM_COL_CLASS}>{line.stock}</td>
                      <td className={NUM_COL_CLASS}>{line.plan}</td>
                      <td className={`${NUM_COL_CLASS} ${needCellClass(line.need)}`}>
                        {formatNeedCell(line.need)}
                      </td>
                      <td className={`${NUM_COL_CLASS} font-semibold`}>{line.totalActual}</td>
                    </tr>
                  ))}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
